import React, { useState, useEffect, Component } from 'react';
import { Box, Typography, Button, CircularProgress, Paper, Chip } from '@mui/material';
import TinderCard from 'react-tinder-card';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api'; 
import { useAuth } from '../../context/AuthContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

// ----------------------------------------------------------------------
// 1. ERROR BOUNDARY: Catches render-phase WSODs
// ----------------------------------------------------------------------
class GameErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, errorInfo: null };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true };
    }
    componentDidCatch(error, errorInfo) {
        console.error("Game Component Crash:", error, errorInfo);
        this.setState({ errorInfo });
    }
    render() {
        if (this.state.hasError) {
            return (
                <Paper sx={{ p: 4, textAlign: 'center', mt: 4, color: 'error.main' }}>
                    <Typography variant="h6">The interface encountered an error.</Typography>
                    <Typography variant="body2">{this.state.errorInfo?.componentStack || 'Module resolution failed.'}</Typography>
                    <Button variant="outlined" sx={{ mt: 2 }} onClick={() => window.location.reload()}>Reload Engine</Button>
                </Paper>
            );
        }
        return this.props.children;
    }
}

// ----------------------------------------------------------------------
// 2. DEFENSIVE MOTION GUARD
// ----------------------------------------------------------------------
const SafeMotionDiv = motion?.div || 'div';

// ----------------------------------------------------------------------
// 3. MAIN COMPONENT
// ----------------------------------------------------------------------
const HybridAptitudeGameBase = () => {
    const authContext = useAuth() || {}; 
    const token = authContext?.token;
    const navigate = useNavigate();
    
    const [gameStage, setGameStage] = useState('intro'); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const [swipes, setSwipes] = useState([]);
    const [scenarios, setScenarios] = useState([]);
    
    const [swipeScores, setSwipeScores] = useState([]);
    const [scenarioScores, setScenarioScores] = useState([]);
    
    const [cardsSwipedCount, setCardsSwipedCount] = useState(0); 
    const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
    const [matchResult, setMatchResult] = useState(null);

    useEffect(() => {
        const fetchGameData = async () => {
            try {
                const res = await api.get('/aptitude/setup', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                // Set the real data from your MySQL database
                if (res.data && res.data.swipes && res.data.scenarios) {
                    setSwipes(res.data.swipes); 
                    setScenarios(res.data.scenarios);
                } else {
                    throw new Error("Invalid setup data payload");
                }
            } catch (err) {
                console.error("Error fetching game data", err);
                setError('Failed to load game data.');
            } finally {
                setLoading(false);
            }
        };
        
        if (token) {
            fetchGameData();
        } else {
            setLoading(false);
        }
    }, [token]);

    const handleSwipe = (direction, cardId, categoryBoost) => {
        const isLiked = direction === 'right';
        setSwipeScores(prev => [...prev, { cardId, categoryBoost, liked: isLiked }]);
        
        setCardsSwipedCount(prevCount => {
            const newCount = prevCount + 1;
            if (swipes?.length > 0 && newCount === swipes.length) {
                setTimeout(() => setGameStage('scenario'), 600); 
            }
            return newCount;
        });
    };

    const handleScenarioChoice = async (scenarioId, chosenTrait) => {
        const updatedScenarioScores = [...scenarioScores, { scenarioId, chosenTrait }];
        setScenarioScores(updatedScenarioScores);

        if (scenarios?.length && currentScenarioIndex + 1 < scenarios.length) {
            setCurrentScenarioIndex(prev => prev + 1);
        } else {
            setGameStage('loading_results');
            try {
                const res = await api?.post('/aptitude/match', 
                    { swipeScores, scenarioScores: updatedScenarioScores },
                    { headers: { Authorization: `Bearer ${token}` }}
                );
                
                if (res?.data && res.data.archetype) {
                    setMatchResult(res.data);
                } else {
                    throw new Error("Invalid response format from server");
                }
                setGameStage('results');
                
            } catch (err) {
                console.error("Error fetching match", err);
                // Guaranteed fallback to prevent blank white screens
                setMatchResult({ 
                    archetype: "The Maverick", 
                    message: "You forge your own path and adapt to any challenge thrown your way."
                });
                setGameStage('results'); 
            }
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
    if (error) return <Typography color="error" align="center">{error}</Typography>;

    const currentScenario = scenarios?.[currentScenarioIndex] || {};

    return (
        <Paper elevation={3} sx={{ maxWidth: 600, mx: 'auto', mt: 4, minHeight: 450, position: 'relative', overflow: 'hidden', borderRadius: 4 }}>
            <AnimatePresence mode='wait'>
                
                {gameStage === 'intro' && (
                    <SafeMotionDiv key="intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, x: -200 }} style={{ padding: '40px', textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" gutterBottom>The Vibe Check</Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            Welcome to the Career Intelligence engine. We're going to map your personality and problem-solving skills to the future of the South African job market.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 4 }}>
                            <strong>Stage 1:</strong> Swipe Right if you like the task, Swipe Left if you hate it. Go fast!
                        </Typography>
                        <Button variant="contained" size="large" onClick={() => setGameStage('swipe')} fullWidth sx={{ py: 1.5, borderRadius: 2 }}>
                            Start Discovery
                        </Button>
                    </SafeMotionDiv>
                )}

                {gameStage === 'swipe' && Array.isArray(swipes) && swipes.length > 0 && (
                    <SafeMotionDiv key="swipe" initial={{ opacity: 0, x: 200 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -200 }} style={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="h6" gutterBottom>Instinct Check</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 3 }}>Swipe Right = Love it | Swipe Left = Nope</Typography>
                        
                        <Box sx={{ position: 'relative', width: 300, height: 300, mx: 'auto' }}>
                            {swipes.map((card) => (
                                <TinderCard 
                                    key={card?.id || Math.random()} 
                                    onSwipe={(dir) => handleSwipe(dir, card?.id, card?.category_boost)} 
                                    preventSwipe={['up', 'down']}
                                    className="tinder-card-wrapper"
                                >
                                    <Paper sx={{ width: 300, height: 280, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, textAlign: 'center', boxShadow: 6, cursor: 'grab', borderRadius: 3 }}>
                                        <Typography variant="h5" fontWeight="bold" color="primary">{card?.task_description || 'Unknown Task'}</Typography>
                                    </Paper>
                                </TinderCard>
                            ))}
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: 250, mt: 4 }}>
                            <CancelIcon color="error" sx={{ fontSize: 40 }} />
                            <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                        </Box>
                    </SafeMotionDiv>
                )}

                {gameStage === 'scenario' && Array.isArray(scenarios) && scenarios.length > 0 && (
                    <SafeMotionDiv key="scenario" initial={{ opacity: 0, x: 200 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -200 }} style={{ padding: '30px' }}>
                        <Typography variant="overline" color="primary" fontWeight="bold">The Deep Dive ({currentScenarioIndex + 1}/{scenarios.length})</Typography>
                        <Typography variant="h6" sx={{ mt: 1, mb: 4, lineHeight: 1.5 }}>
                            {currentScenario?.scenario_text}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button variant="outlined" sx={{ py: 2, justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => handleScenarioChoice(currentScenario?.id, currentScenario?.option_a_trait)}>
                                {currentScenario?.option_a || 'Option A'}
                            </Button>
                            <Button variant="outlined" sx={{ py: 2, justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => handleScenarioChoice(currentScenario?.id, currentScenario?.option_b_trait)}>
                                {currentScenario?.option_b || 'Option B'}
                            </Button>
                            <Button variant="outlined" sx={{ py: 2, justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => handleScenarioChoice(currentScenario?.id, currentScenario?.option_c_trait)}>
                                {currentScenario?.option_c || 'Option C'}
                            </Button>
                            <Button variant="outlined" sx={{ py: 2, justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => handleScenarioChoice(currentScenario?.id, currentScenario?.option_d_trait)}>
                                {currentScenario?.option_d || 'Option D'}
                            </Button>
                        </Box>
                    </SafeMotionDiv>
                )}

                {gameStage === 'loading_results' && (
                    <SafeMotionDiv key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ padding: '40px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={60} sx={{ mb: 3 }} />
                        <Typography variant="h6">Analyzing your traits...</Typography>
                        <Typography variant="body2" color="text.secondary">Consulting Gemini AI to find your golden matches.</Typography>
                    </SafeMotionDiv>
                )}

                {gameStage === 'results' && matchResult && (
                    <SafeMotionDiv key="results" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} style={{ padding: '40px', textAlign: 'center' }}>
                        <Typography variant="h5" gutterBottom>Your Career Archetype:</Typography>
                        <Chip label={matchResult?.archetype || 'Unknown'} color="primary" sx={{ fontSize: 24, p: 3, mb: 4, fontWeight: 'bold' }} />
                        
                        <Typography variant="body1" paragraph>
                            {matchResult?.message || ''}
                        </Typography>

                        <Button variant="contained" fullWidth onClick={() => navigate('/applicant/dashboard')} sx={{ mt: 3 }}>
                            Return to Dashboard
                        </Button>
                    </SafeMotionDiv>
                )}
            </AnimatePresence>
        </Paper>
    );
};

// ----------------------------------------------------------------------
// 4. EXPORT: Wrapped safely in the Error Boundary
// ----------------------------------------------------------------------
export default function HybridAptitudeGame(props) {
    return (
        <GameErrorBoundary>
            <HybridAptitudeGameBase {...props} />
        </GameErrorBoundary>
    );
}